import React, { useState, useEffect, FC } from 'react';
import { View } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import moment from 'moment';

import { fullWidth, scale } from '@components/frame';
import { isWeb, isPhone, machineStatus } from '@helpers';
import AnimatedHeader from '@components/common/AnimatedHeader';
import { SmallLoading } from '@components/loading';
import { getBoxShadowStyle } from '@styles/shadow';

import Active from './Active';
import NotProductive from './NotProductive';

import ColorWrapper from '@styles/colors';
import EditTime from './EditTime';

import { machineEventDetails, getNCFileByName } from '@reducers/manufacture';
import { EventDetailsProps } from '@v2/pages/machines/common/types/event';
import { useNavigation, useRoute } from '@react-navigation/native';
import Change from './Change';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

const EventDetailsComponent: FC<EventDetailsProps> = ({ C, machineEventDetails, getNCFileByName, selectedItem }) => {
  const route = useRoute();

  const { IS_NARROW_WEB, detailsWidth } = useDeviceSize();

  const [showEditTime, setShowEditTime] = useState(false);
  const eventDetails = {
    sequenceNumberStartedOn: route.params?.eventDetailsStart,
    sequenceNumberEndedOn: route.params?.eventDetailsEnd,
  };

  const [eventInfo, setEventInfo] = useState(eventDetails || {});
  const [NCFileInfo, setNCFileInfo] = useState({});

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const navigation = useNavigation();

  useEffect(() => {
    (async () => {
      const data = await machineEventDetails({ ...eventDetails, machineId: selectedItem.machineId });
      const ncFile = await getNCFileByName(data.ncFileMain);
      const event = {
        ...data?.[0],
        durationInMilliseconds: Math.round(moment.duration(moment(data?.[0].endedOn).diff(moment(data?.[0].startedOn))).asMilliseconds()),
      };
      setEventInfo(event);
      setNCFileInfo(ncFile.searchResult[0] || {});
    })();
  }, []);

  const renderComponent = () => {
    const onNcDetailsClick = (name: string) => {
      navigateToMainDetailsScreen({
        mainScreen: NCFileDetailsScreenName,
        subScreen: NCFileDetailsScreen.NC_FILE,
        params: {
          perspective: Perspective.NC_FILE,
          ncFileName: name,
          ncFileId: eventInfo?.ncFileIdMain,
          ncFileStatus: NCFileInfo?.ncFileStatusMain,
        },
      });
    };

    const props = { C, selectedItem, setShowEditTime, eventInfo, NCFileInfo, onNcDetailsClick };

    switch (eventInfo.status) {
      case machineStatus.ACTIVE:
        return <Active {...props} />;
      case machineStatus.CHANGE:
        return <Change {...props} />;
      case machineStatus.NOTPRODUCTIVE:
        return <NotProductive {...props} />;
      default:
        return <NotProductive {...props} />;
    }
  };
  return [
    showEditTime && <EditTime key="EditEventTime" {...{ C, setShowEditTime, ...showEditTime, eventInfo }} />,
    <View
      key="EventDetails"
      style={{
        flex: 1,
        backgroundColor: C('Background/Light/Detail'),
        width: isPhone ? '100%' : detailsWidth - 1,
        right: 0,
      }}
    >
      <AnimatedHeader
        {...{
          text: i18n.t('Event'),
          C,
          animated: false,
          back: true,
          backgroundColor: 'Background/Light/Detail',
          borderBottomWidth: 0,
          onBackPress: () => {
            navigation.goBack();
          },
          customMargin: 0,
          customPadding: 0,
        }}
      />
      <View style={{ width: '100%', alignItems: 'center', marginTop: scale(21), paddingTop: scale(41) }}>
        {eventInfo.status ? (
          <View
            style={[
              isPhone && !IS_NARROW_WEB
                ? {
                    width: '100%',
                    paddingHorizontal: scale(15),
                  }
                : IS_NARROW_WEB
                ? {
                    width: fullWidth - 30,
                  }
                : { width: scale(450) },
              isWeb && { borderRadius: scale(SCREEN_BORDER_RADIUS) },
              isWeb && getBoxShadowStyle({ type: 'Field', C }),
            ]}
          >
            {renderComponent()}
          </View>
        ) : (
          <SmallLoading />
        )}
      </View>
    </View>,
  ];
};

export default connect(({ machines: { selectedItem } }) => ({ selectedItem }), { machineEventDetails, getNCFileByName })(
  ColorWrapper(EventDetailsComponent)
);
